import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES, type UserRole } from '@/lib/constants/roles';
import {
  WIDGETS,
  defaultLayoutFor,
  type LayoutItem,
  type LayoutScope,
  type WidgetId,
  type WidgetSize,
} from './config';

/**
 * Bump on every default-layout breaking change so previously-saved layouts
 * don't shadow the new seed.
 *   v2 → switched layout model from x/y/w/h to {id, size}.
 *   v3 → introduced `xs` KPI-tile size (6-per-row default) + new design
 *        widget seeds.
 *   v4 → re-seed to lock KPI strip at 2 rows / 6-per-row at lg.
 */
const STORAGE_VERSION = 4;
function storageKey(userId: string | null, scope: LayoutScope) {
  return `obd3:dashboard:v${STORAGE_VERSION}:${userId ?? 'anon'}:${scope}`;
}

function isValidItem(it: unknown): it is LayoutItem {
  return (
    typeof it === 'object' &&
    it !== null &&
    'id' in it &&
    'size' in it &&
    WIDGETS.some((w) => w.id === (it as { id: WidgetId }).id)
  );
}

/*
 * KPI tiles must always render at `xs` so the strip stays at 6 tiles per
 * row (2 rows for 12 tiles). Even if a stored layout carries an older
 * size like `sm`, we coerce KPI items to `xs` on load — the strip's
 * shape is a product decision, not a user preference.
 */
function coerceKpiSizes(items: LayoutItem[], scope: LayoutScope): LayoutItem[] {
  if (scope !== 'kpi') return items;
  return items.map((it) => (it.size === 'xs' ? it : { ...it, size: 'xs' }));
}

function loadLayout(
  userId: string | null,
  role: UserRole,
  scope: LayoutScope,
): LayoutItem[] {
  if (typeof window === 'undefined') return defaultLayoutFor(role, scope);
  try {
    const raw = window.localStorage.getItem(storageKey(userId, scope));
    if (!raw) return defaultLayoutFor(role, scope);
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return defaultLayoutFor(role, scope);
    return coerceKpiSizes(parsed.filter(isValidItem), scope);
  } catch {
    return defaultLayoutFor(role, scope);
  }
}

function saveLayout(
  userId: string | null,
  scope: LayoutScope,
  layout: LayoutItem[],
) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      storageKey(userId, scope),
      JSON.stringify(layout),
    );
  } catch {
    /* quota / disabled — ignore */
  }
}

function useResolvedRole(): UserRole {
  const role = useAuthStore((s) => s.role) as UserRole;
  const isSubSuper = useAuthStore((s) => s.isSubSuperAdmin());
  return isSubSuper ? USER_ROLES.SUB_SUPER_ADMIN : role;
}

export function useDashboardLayout(scope: LayoutScope) {
  const userId = useAuthStore((s) => s.userId);
  const role = useResolvedRole();

  const [layout, setLayout] = useState<LayoutItem[]>(() =>
    loadLayout(userId, role, scope),
  );

  useEffect(() => {
    setLayout(loadLayout(userId, role, scope));
  }, [userId, role, scope]);

  const replace = useCallback(
    (next: LayoutItem[]) => {
      setLayout(next);
      saveLayout(userId, scope, next);
    },
    [userId, scope],
  );

  const reorder = useCallback(
    (fromId: WidgetId, toId: WidgetId) => {
      const fromIdx = layout.findIndex((it) => it.id === fromId);
      const toIdx = layout.findIndex((it) => it.id === toId);
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
      const next = [...layout];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved!);
      replace(next);
    },
    [layout, replace],
  );

  const setSize = useCallback(
    (id: WidgetId, size: WidgetSize) => {
      replace(layout.map((it) => (it.id === id ? { ...it, size } : it)));
    },
    [layout, replace],
  );

  const add = useCallback(
    (id: WidgetId) => {
      const desc = WIDGETS.find((w) => w.id === id);
      if (!desc) return;
      const exists = layout.some((it) => it.id === id);
      if (exists) return;
      replace([...layout, { id, size: desc.defaultSize }]);
    },
    [layout, replace],
  );

  const remove = useCallback(
    (id: WidgetId) => {
      replace(layout.filter((it) => it.id !== id));
    },
    [layout, replace],
  );

  const reset = useCallback(() => {
    const next = defaultLayoutFor(role, scope);
    setLayout(next);
    saveLayout(userId, scope, next);
  }, [role, scope, userId]);

  return { layout, replace, reorder, setSize, add, remove, reset, role };
}
